import {
  Document,
  Image,
  Page,
  pdf,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer'
import { splitOfferBoldSegments } from '@proffer/shared'
import { registerNotoSansPdfFonts } from '../pdf/registerNotoSans'
import type { OfferDto } from '../types/offer'
import {
  formatBusinessAddressLines,
  type BusinessPdfProfile,
} from '../utils/businessProfile'
import { formatDateEl } from './UI'
import { offerPdfFilename } from '../utils/offerShareText'

registerNotoSansPdfFonts()

function formatEuroPdf(amount: string | null): string {
  if (amount == null || amount === '') return '—'
  const n = Number(amount)
  if (Number.isNaN(n)) return `${amount} €`
  return new Intl.NumberFormat('el-GR', {
    style: 'currency',
    currency: 'EUR',
  }).format(n)
}

function documentAuthor(profile: BusinessPdfProfile): string | undefined {
  const n = profile.businessName?.trim()
  if (n) return n
  const bio = profile.business_info?.trim()
  if (bio) return bio
  return undefined
}

const styles = StyleSheet.create({
  page: {
    fontFamily: 'NotoSans',
    fontSize: 11,
    paddingTop: 36,
    paddingBottom: 48,
    paddingHorizontal: 44,
    lineHeight: 1.45,
    color: '#18181b',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 22,
    minHeight: 64,
  },
  headerLeft: {
    width: '38%',
    maxWidth: 130,
    justifyContent: 'flex-start',
  },
  headerRight: {
    width: '58%',
    alignItems: 'flex-end',
  },
  logo: {
    width: 118,
    height: 56,
    objectFit: 'contain',
  },
  companyName: {
    fontSize: 11,
    fontWeight: 700,
    textAlign: 'right',
    marginBottom: 4,
    color: '#27272a',
  },
  companyLine: {
    fontSize: 9,
    textAlign: 'right',
    color: '#52525b',
    marginBottom: 2,
  },
  companyMultiline: {
    fontSize: 9,
    textAlign: 'right',
    color: '#52525b',
    marginBottom: 2,
    lineHeight: 1.35,
  },
  sectionLabel: {
    fontSize: 9,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: '#71717a',
    marginBottom: 6,
  },
  block: {
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 8,
    color: '#18181b',
  },
  meta: {
    fontSize: 10,
    color: '#52525b',
    marginBottom: 4,
  },
  bodyLabel: {
    fontSize: 9,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: '#71717a',
    marginTop: 8,
    marginBottom: 8,
  },
  bodyLine: {
    fontSize: 11,
    marginBottom: 3,
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#e4e4e7',
    marginVertical: 16,
  },
})

function HeaderBlock({ profile }: { profile: BusinessPdfProfile }) {
  const {
    logoAbsoluteUrl,
    businessName,
    vatNumber,
    businessEmail,
    phone,
    mobile,
    gemiNumber,
    taxOffice,
    businessActivity,
    business_info,
  } = profile

  const addrLines = formatBusinessAddressLines(profile)
  const hasStructured =
    !!businessName?.trim() ||
    !!vatNumber?.trim() ||
    addrLines.length > 0 ||
    !!phone?.trim() ||
    !!mobile?.trim() ||
    !!businessEmail?.trim() ||
    !!gemiNumber?.trim() ||
    !!taxOffice?.trim() ||
    !!businessActivity?.trim()
  const fallbackInfo = !hasStructured && business_info?.trim()

  return (
    <View style={styles.headerRow}>
      <View style={styles.headerLeft}>
        {logoAbsoluteUrl ? (
          <Image src={logoAbsoluteUrl} style={styles.logo} />
        ) : null}
      </View>
      <View style={styles.headerRight}>
        {businessName?.trim() ? (
          <Text style={styles.companyName}>{businessName.trim()}</Text>
        ) : null}
        {vatNumber?.trim() ? (
          <Text style={styles.companyLine}>
            ΑΦΜ: {vatNumber.trim()}
          </Text>
        ) : null}
        {addrLines.map((line, i) => (
          <Text key={i} style={styles.companyLine}>
            {line}
          </Text>
        ))}
        {gemiNumber?.trim() ? (
          <Text style={styles.companyLine}>ΓΕΜΗ: {gemiNumber.trim()}</Text>
        ) : null}
        {taxOffice?.trim() ? (
          <Text style={styles.companyLine}>ΔΟΥ: {taxOffice.trim()}</Text>
        ) : null}
        {businessActivity?.trim() ? (
          <Text style={styles.companyMultiline}>{businessActivity.trim()}</Text>
        ) : null}
        {businessEmail?.trim() ? (
          <Text style={styles.companyLine}>{businessEmail.trim()}</Text>
        ) : null}
        {phone?.trim() ? (
          <Text style={styles.companyLine}>Τηλ.: {phone.trim()}</Text>
        ) : null}
        {mobile?.trim() ? (
          <Text style={styles.companyLine}>Κιν.: {mobile.trim()}</Text>
        ) : null}
        {fallbackInfo ? (
          <Text style={styles.companyMultiline}>{fallbackInfo}</Text>
        ) : null}
        {!hasStructured && !fallbackInfo && !logoAbsoluteUrl ? (
          <Text style={styles.companyLine}>Προσφορά</Text>
        ) : null}
      </View>
    </View>
  )
}

export type OfferPdfDocumentProps = {
  offer: OfferDto
  profile: BusinessPdfProfile
}

export function OfferPdfDocument({ offer, profile }: OfferPdfDocumentProps) {
  const created = formatDateEl(offer.createdAt)
  const body = offer.generated_text?.trim() ?? ''
  const lines = body.length > 0 ? body.split(/\r?\n/) : ['—']

  return (
    <Document
      title={offer.title}
      author={documentAuthor(profile)}
      language="el-GR"
    >
      <Page size="A4" style={styles.page} wrap>
        <HeaderBlock profile={profile} />

        <View style={styles.block}>
          <Text style={styles.sectionLabel}>Πελάτης</Text>
          <Text style={styles.bodyLine}>{offer.customer.name}</Text>
          {offer.customer.phone ? (
            <Text style={styles.bodyLine}>Τηλ.: {offer.customer.phone}</Text>
          ) : null}
          {offer.customer.email ? (
            <Text style={styles.bodyLine}>Email: {offer.customer.email}</Text>
          ) : null}
        </View>

        <View style={styles.divider} />

        <Text style={styles.title}>{offer.title}</Text>
        <Text style={styles.meta}>Ημερομηνία: {created}</Text>
        <Text style={styles.meta}>
          Συνολικό ποσό: {formatEuroPdf(offer.amount)}
        </Text>

        <Text style={styles.bodyLabel}>Κείμενο προσφοράς</Text>
        {lines.map((line, i) => (
          <Text key={i} style={styles.bodyLine}>
            {line.length > 0
              ? splitOfferBoldSegments(line).map((seg, j) => (
                  <Text
                    key={j}
                    style={seg.bold ? { fontFamily: 'Helvetica-Bold' } : undefined}
                  >
                    {seg.text}
                  </Text>
                ))
              : ' '}
          </Text>
        ))}
      </Page>
    </Document>
  )
}

export async function downloadOfferPdf(
  offer: OfferDto,
  profile: BusinessPdfProfile,
): Promise<void> {
  const doc = pdf(<OfferPdfDocument offer={offer} profile={profile} />)
  const blob = await doc.toBlob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = offerPdfFilename(offer)
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
